import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Baby, Gift } from "lucide-react";
import axios from "axios";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// audience: "genitori" | "tate" | "entrambi"
const SERVICES = [
  {
    n: "01",
    title: "Consulenza sul Sonno e Addormentamento",
    desc: "Percorso personalizzato per accompagnare il sonno del neonato e del bambino con approccio dolce e rispettoso dei suoi tempi.",
    for: "Genitori di neonati e bambini fino ai 3 anni.",
    audience: "genitori",
    infoLink: "https://forms.gle/WhBhzbXJM5KBEUjr5",
  },
  {
    n: "02",
    title: "Consulenza Educatrice Neonatale",
    desc: "Supporto competente e rispettoso nella cura del neonato: routine, benessere, primi mesi. Con lo sguardo di un'educatrice neonatale.",
    for: "Genitori nella fascia 0–6 mesi.",
    audience: "genitori",
    infoLink: "https://forms.gle/HJPqx7SNV1n5SqUr6",
  },
  {
    n: "03",
    title: "Massaggio Infantile & Tocco",
    desc: "Incontri di massaggio infantile e tocco relazionale per sostenere il legame profondo tra genitore e bambino e favorire lo sviluppo psicomotorio.",
    for: "Neonati e bambini con i loro genitori.",
    audience: "genitori",
    infoLink: "https://forms.gle/vkSVJy2oRwtxzxdH6",
  },
  {
    n: "04",
    title: "Psicomotricità su Misura",
    desc: "Percorsi di psicomotricità personalizzati per accompagnare lo sviluppo motorio, sensoriale ed emotivo del bambino nel rispetto dei suoi tempi.",
    for: "Bambini in età evolutiva.",
    audience: "genitori",
    infoLink: "https://forms.gle/8M8kDEneyv7FXuSHA",
  },
  {
    n: "05",
    title: "Family Coach Montessori",
    desc: "Consulenza personalizzata su ambiente domestico, materiali, proposte di gioco a misura di età. Portiamo il Metodo Montessori nella quotidianità.",
    for: "Famiglie curiose di un approccio Montessori autentico.",
    audience: "genitori",
    infoLink: "https://forms.gle/z2EUECTsUEjQafdL9",
  },
  {
    n: "06",
    title: "Abbandono del Ciuccio",
    desc: "Percorso di abbandono dolce del ciuccio. Nessuna forzatura: solo strategie concrete che rispettano il ritmo di ogni bambino.",
    for: "Famiglie pronte a un passaggio delicato.",
    audience: "genitori",
    infoLink: "https://form.jotform.com/261496243162053",
  },
  {
    n: "07",
    title: "Abbandono del Pannolino",
    desc: "Percorso di spannolinamento graduale e rispettoso dei tempi del bambino, senza pressioni né confronti.",
    for: "Bambini pronti al passaggio, con il sostegno dei genitori.",
    audience: "genitori",
    infoLink: "https://forms.gle/BFKvpqUyoyeBUHRG7",
  },
  {
    n: "08",
    title: "Laboratori & Atelier",
    desc: "Esperienze creative e sensoriali strutturate, pensate per accompagnare bambini e famiglie in un tempo condiviso di scoperta.",
    for: "Bambini 0–6 anni con genitore.",
    audience: "genitori",
    infoLink: "https://forms.gle/pEXrSErSq7NeDnX66",
  },
  {
    n: "09",
    title: "Laboratori Manuali",
    desc: "Attività manuali dedicate ai bambini per sviluppare motricità fine, autonomia e creatività attraverso il fare.",
    for: "Bambini in età prescolare.",
    audience: "genitori",
    infoLink: "https://forms.gle/op851kWib98onArC8",
  },
  {
    n: "10",
    title: "Feste & Compleanni",
    desc: "Ideazione e organizzazione di feste, laboratori ed eventi. Momenti curati nei dettagli, dove il gioco è cuore dell'esperienza.",
    for: "Compleanni, ricorrenze, eventi speciali.",
    audience: "genitori",
    link: "/feste",
    ctaLabel: "Scopri le feste",
    infoLink: "https://forms.gle/VYacpwqntRrKpnFNA",
  },
  {
    n: "11",
    title: "Percorso di Consulenza Educativa Personalizzata",
    desc: "Un percorso di accompagnamento educativo continuativo, costruito su misura dei bisogni della tua famiglia e del tuo bambino. Osservazione, obiettivi, restituzioni.",
    for: "Famiglie che desiderano un percorso strutturato nel tempo.",
    audience: "genitori",
    infoLink: "https://form.jotform.com/252926943498373",
  },
  {
    n: "12",
    title: "Percorsi di Supporto Genitori-Bambini",
    desc: "Un cammino condiviso tra genitori e bambini per rafforzare il legame, superare piccoli passaggi delicati e ritrovare fiducia nel proprio ruolo educativo.",
    for: "Famiglie che vogliono lavorare insieme al proprio bambino.",
    audience: "genitori",
    infoLink: "https://form.jotform.com/260646037296058",
  },
  {
    n: "13",
    title: "Corso: Ginnastica Neonatale e Fondamenti di Psicomotricità",
    desc: "Un percorso pratico per genitori dedicato alla ginnastica neonatale e ai fondamenti della psicomotricità. Movimenti, tocco, sostegno allo sviluppo motorio.",
    for: "Genitori di neonati e bambini piccoli.",
    audience: "genitori",
    infoLink: "https://form.jotform.com/260635283728059",
  },
  {
    n: "14",
    title: "Trasferte & Cura Fuori Domicilio",
    desc: "Assistenza e cura educativa anche fuori dal domicilio abituale: trasferta oraria, giornaliera, weekend, notte in trasferta o ore extra. Valida per tutte le fasce d'età dei bambini, sempre nel rispetto della famiglia.",
    for: "Famiglie di bambini di ogni età che hanno bisogno di supporto fuori casa.",
    audience: "genitori",
    infoLink: BRAND.whatsappUrl + "?text=" + encodeURIComponent("Ciao Teresa, vorrei info sulle Trasferte & Cura Fuori Domicilio 🚗"),
  },
  // TATE
  {
    n: "T1",
    title: "Become Tata like a Pro",
    desc: "Percorso professionalizzante completo per diventare una tata professionista: competenze pedagogiche, comunicazione con le famiglie, aspetti pratici e contrattuali.",
    for: "Tate e babysitter che vogliono crescere professionalmente.",
    audience: "tate",
    infoLink: "https://forms.gle/X1De8dfcU6ZoJD3U7",
  },
  {
    n: "T2",
    title: "Consulenza per Tata",
    desc: "Uno spazio di confronto individuale per riflettere sul proprio ruolo, chiarire dubbi e ricevere strumenti operativi personalizzati.",
    for: "Tate in attività, esperienze consolidate o alle prime esperienze.",
    audience: "tate",
    infoLink: "https://forms.gle/hYavwy7Hz6SPRYkn8",
  },
  {
    n: "T3",
    title: "Corso Professionalizzante: Il Sonno dei Piccoli",
    desc: "Formazione specialistica dedicata al sonno del neonato e del bambino. Osservazione, strategie, competenze concrete per accompagnare notti serene.",
    for: "Tate e babysitter che vogliono specializzarsi sul sonno infantile.",
    audience: "tate",
    infoLink: "https://forms.gle/TCnteXCNDiFQBBJ19",
  },
];

const FILTERS = [
  { key: "genitori", label: "Per Genitori", icon: HeartHandshake, color: "#FF2768" },
  { key: "tate", label: "Per Tate", icon: Baby, color: "#81C3C9" },
];

export default function Servizi() {
  const [audience, setAudience] = useState("genitori");
  const [tataLive, setTataLive] = useState(false);
  useEffect(() => {
    // La card "Become Tata like a Pro" porta alla landing dedicata solo quando è pubblicata
    axios.get(`${API}/tata-landing/status`).then((r) => setTataLive(!!r.data?.published)).catch(() => {});
  }, []);
  const filtered = SERVICES.filter((s) => s.audience === audience);
  const activeFilter = FILTERS.find((f) => f.key === audience);

  return (
    <div className="pt-32 md:pt-40 pb-16 px-6 md:px-10" data-testid="servizi-page">
      <div className="max-w-[1600px] mx-auto">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">— Cosa posso fare per te</p>
        <h1 className="font-display text-[14vw] md:text-[10vw] lg:text-[12rem] leading-[0.85] text-[#3D2140] mb-6">
          <RevealText text={["Percorsi", "su misura."]} />
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 items-center">
          <div className="md:col-span-7">
            <p className="max-w-2xl text-lg text-[#3D2140]/70 leading-relaxed font-editorial text-2xl md:text-3xl">
              Ogni percorso è unico. Qui trovi consulenze personalizzate per <strong>genitori</strong> e corsi professionali per <strong>tate</strong>.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end">
            <img
              src={BRAND.photos.workingStudio}
              alt="Teresa Vilardo al lavoro"
              className="w-full max-w-md aspect-[4/5] object-cover rounded-[36px] shadow-2xl border-4 border-white"
              data-testid="servizi-hero-photo"
            />
          </div>
        </div>

        {/* FEATURED: Cura Educativa a Domicilio */}
        <Link
          to="/cura-domicilio"
          data-testid="featured-cura-domicilio"
          className="group block relative overflow-hidden rounded-[40px] bg-gradient-to-br from-[#FF2768] to-[#C81E52] text-white p-10 md:p-16 mb-20 hover:shadow-2xl transition-all duration-500"
        >
          <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute -right-8 -bottom-8 h-56 w-56 rounded-full bg-[#FFE45C]/25 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] mb-4">— Il mio percorso principale · 0–36 mesi</p>
              <h3 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.88] mb-6">
                Cura Educativa<br/>a <span className="text-[#FFE45C]">Domicilio</span>
              </h3>
              <p className="text-white/85 text-lg max-w-xl leading-relaxed mb-8">
                Due percorsi (0–6 mesi e 6–36 mesi), un unico approccio: il <strong>Metodo T.A.T.A.®</strong>. Presenza educativa qualificata, personalizzata, continuativa.
              </p>
              <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#FF2768] font-semibold group-hover:gap-4 transition-all">
                Scopri il percorso completo <ArrowRight size={16} />
              </span>
            </div>
            <div className="md:col-span-3 md:col-start-10 flex justify-end">
              <img src={BRAND.logo} alt="" aria-hidden className="h-40 md:h-56 w-40 md:w-56 rounded-full animate-floaty drop-shadow-2xl" />
            </div>
          </div>
        </Link>

        {/* FEATURED: Assistenza Neonatale */}
        <Link
          to="/assistenza-neonatale"
          data-testid="featured-assistenza-neonatale"
          className="group block relative overflow-hidden rounded-[40px] bg-gradient-to-br from-[#3D2140] to-[#3D2140] text-white p-10 md:p-14 mb-20 hover:shadow-2xl transition-all duration-500 border border-white/5"
        >
          <div className="absolute -left-16 -top-16 h-64 w-64 rounded-full bg-[#FF2768]/25 blur-3xl pointer-events-none" />
          <div className="absolute -right-12 -bottom-12 h-56 w-56 rounded-full bg-[#81C3C9]/20 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4 font-semibold">— Assistenza Neonatale Specialistica · 0-6 mesi</p>
              <h3 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] mb-5">
                I primi mesi<br/><span className="text-[#81C3C9]">insieme.</span>
              </h3>
              <p className="text-white/85 text-base md:text-lg max-w-xl leading-relaxed mb-6">
                Quattro percorsi dedicati alle famiglie con neonati: <strong>diurno, notturno, integrato, continuativo</strong>. Con pacchetti dedicati e possibilità di trasferta.
              </p>
              <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2768] text-white font-semibold group-hover:gap-4 transition-all">
                Scopri i percorsi <ArrowRight size={16} />
              </span>
            </div>
            <div className="md:col-span-3 md:col-start-10 flex justify-end">
              <img src={BRAND.logo} alt="" aria-hidden className="h-32 md:h-44 w-32 md:w-44 rounded-full animate-floaty opacity-90 drop-shadow-2xl" />
            </div>
          </div>
        </Link>

        {/* AUDIENCE TABS */}
        <div className="mb-10" data-testid="audience-tabs">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Scegli il tuo pubblico</p>
          <div className="flex flex-wrap gap-3">
            {FILTERS.map((f) => {
              const Icon = f.icon;
              const active = audience === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setAudience(f.key)}
                  data-testid={`tab-${f.key}`}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold uppercase tracking-wider transition-all ${
                    active
                      ? "text-white shadow-xl"
                      : "bg-transparent text-[#3D2140] border border-[#3D2140]/25 hover:border-[#FF2768] hover:text-[#FF2768]"
                  }`}
                  style={active ? { background: f.color } : {}}
                >
                  <Icon size={16} /> {f.label}
                </button>
              );
            })}
            <Link
              to="/voucher-regalo"
              data-testid="tab-regali-cura"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold uppercase tracking-wider bg-transparent text-[#3D2140] border border-dashed border-[#FF2768]/60 hover:border-solid hover:bg-[#FF2768] hover:text-white hover:border-[#FF2768] transition-all"
            >
              <Gift size={16} /> Regali di Cura
            </Link>
          </div>
        </div>

        {/* SERVICES GRID */}
        <AnimatePresence mode="wait">
          <motion.div
            key={audience}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16"
          >
            {filtered.map((s, idx) => (
              <motion.article
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: (idx % 2) * 0.08 }}
                className={`group relative ${idx % 2 === 1 ? "md:mt-16" : ""}`}
                data-testid={`service-card-${s.n}`}
              >
                <div className="flex items-baseline gap-6 mb-6">
                  <span className="font-editorial text-[#FF2768] text-2xl">{s.n}</span>
                  <div className="h-px flex-1 bg-[#3D2140]/15" />
                </div>
                <h2 className="font-display text-5xl md:text-6xl text-[#3D2140] mb-6 group-hover:text-[#FF2768] transition-colors duration-500">
                  {s.title}
                </h2>
                <p className="text-[#3D2140]/80 text-lg leading-relaxed mb-4">{s.desc}</p>
                <p className="text-sm text-[#81C3C9] font-medium mb-6 italic">{s.for}</p>
                <div className="flex flex-wrap gap-3">
                  {s.link && (
                    <Link to={s.link} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#3D2140]/25 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768] hover:text-[#FF2768] transition-colors">
                      {s.ctaLabel} <ArrowRight size={14} />
                    </Link>
                  )}
                  {s.n === "T1" && tataLive && (
                    <Link
                      to="/become-tata-like-a-pro"
                      data-testid="service-landing-T1"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3D2140] text-white text-sm font-semibold hover:bg-[#FF2768] transition-colors"
                    >
                      Scopri il corso <ArrowRight size={14} />
                    </Link>
                  )}
                  {s.infoLink && (
                    <a
                      href={s.infoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`service-info-${s.n}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#C81E52] transition-colors"
                    >
                      Richiedi info <ArrowRight size={14} />
                    </a>
                  )}
                  {!s.link && !s.infoLink && (
                    <Link to="/prenota" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#C81E52] transition-colors">
                      Contattami <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </motion.article>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* FEATURED: Regali di Cura */}
        <Link
          to="/voucher-regalo"
          data-testid="featured-regali-cura"
          className="mt-32 group block relative overflow-hidden rounded-[40px] bg-gradient-to-br from-[#3D2140] to-[#3D2140] text-white p-10 md:p-16 hover:shadow-2xl transition-all duration-500 border border-white/5"
        >
          <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-[#FF2768]/25 blur-3xl pointer-events-none" />
          <div className="absolute -right-12 -bottom-12 h-64 w-64 rounded-full bg-[#81C3C9]/20 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4 font-semibold">
                <Gift size={14} /> — Regali di Cura
              </p>
              <h3 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.88] mb-6">
                Regala tempo.<br/>
                <span className="text-[#FF2768]">Regala serenità.</span>
              </h3>
              <p className="text-white/85 text-lg max-w-xl leading-relaxed mb-8">
                Alcuni dei miei percorsi si trasformano in un pensiero da donare — per una nascita, un compleanno, il Natale o un semplice gesto d'affetto. Regali che accompagnano davvero.
              </p>
              <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2768] text-white font-semibold group-hover:gap-4 transition-all">
                Scopri i Regali di Cura <ArrowRight size={16} />
              </span>
            </div>
            <div className="md:col-span-3 md:col-start-10 flex justify-end">
              <div className="relative">
                <img
                  src={BRAND.logo}
                  alt=""
                  aria-hidden
                  className="h-40 md:h-56 w-40 md:w-56 rounded-full animate-floaty drop-shadow-2xl"
                />
                <div className="absolute -top-3 -right-3 bg-[#FF2768] text-white rounded-full h-14 w-14 flex items-center justify-center shadow-xl">
                  <Gift size={24} />
                </div>
              </div>
            </div>
          </div>
        </Link>

        <div className="mt-24 relative overflow-hidden rounded-[40px] bg-[#81C3C9]/25 text-[#3D2140] p-10 md:p-16 border border-[#81C3C9]/40">
          <img src={BRAND.logo} alt="" aria-hidden className="absolute -right-12 -bottom-12 h-72 w-72 opacity-25" />
          <div className="relative max-w-2xl">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4 font-semibold">— Non trovi quello che cerchi?</p>
            <h3 className="font-display text-6xl md:text-7xl leading-[0.9] mb-6">Parliamone insieme.</h3>
            <p className="text-[#3D2140]/75 text-lg mb-8 leading-relaxed">
              Ogni situazione merita ascolto. Scrivimi per una prima chiacchierata gratuita: capiremo insieme se e come posso esserti utile.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="servizi-whatsapp" className="btn-tv-primary">
                Scrivimi su WhatsApp
              </a>
              <Link to="/prenota" data-testid="servizi-cta-booking" className="btn-tv-outline">
                Compila il form <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
